import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from './Icon';
import Button from './Button';

/**
 * Putting a selection into a pool.
 *
 * This was `window.prompt` — a browser dialog in the middle of a designed
 * application, which cannot say how many leads it is about to save, cannot
 * validate a name, and looks like a bug.
 *
 * It also only ever created. Adding forty leads to a pool you already have is
 * the obvious next thing, and it was impossible without making a duplicate and
 * tidying up afterwards, so both live here.
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(4, 6, 14, 0.6);
  display: grid;
  place-items: center;
  padding: 2rem;
  z-index: 1200;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 440px;
  background: var(--surface-1);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 1.4rem;
`;

const Title = styled.h2`
  font-family: var(--font-heading);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.3rem;
`;

const Lede = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.55;
  margin: 0 0 1.1rem;
`;

const Choice = styled.label`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.7rem;
  border-radius: var(--radius-md);
  border: 1px solid ${(p) => (p.$on ? 'var(--border-accent)' : 'var(--border-color)')};
  background: ${(p) => (p.$on ? 'var(--accent-cyan-dim)' : 'transparent')};
  color: var(--text-primary);
  font-size: var(--text-sm);
  cursor: pointer;
  margin-bottom: 0.4rem;

  input[type="radio"] { width: auto; margin: 0; accent-color: var(--accent-cyan); }
`;

const Count = styled.span`
  margin-left: auto;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
`;

const Input = styled.input`
  width: 100%;
  background: var(--surface-3);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.6rem 0.75rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: var(--text-sm);
  margin-top: 0.5rem;

  &:focus { outline: none; border-color: var(--border-accent); }
`;

const Row = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.2rem;
`;

const Problem = styled.div`
  border: 1px solid rgba(248, 113, 113, 0.4);
  background: var(--error-dim);
  border-radius: var(--radius-md);
  padding: 0.6rem 0.8rem;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  margin-top: 0.8rem;
`;

const Scroll = styled.div`
  max-height: 34vh;
  overflow-y: auto;
`;

function SaveToPoolModal({ count, pools, onClose, onSave }) {
  const [target, setTarget] = useState('new');
  const [name, setName] = useState(`Selection of ${count}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    try {
      setBusy(true);
      setError('');
      await onSave(target === 'new' ? { name: name.trim() } : { listId: target });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save those leads');
      setBusy(false);
    }
  };

  const ready = target !== 'new' || name.trim().length > 0;

  return (
    <Backdrop onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Save leads to a pool">
        <Title>Save {count.toLocaleString()} lead{count === 1 ? '' : 's'}</Title>
        <Lede>A pool is a grouping over the same records — a lead can be in several, and deleting a pool never deletes anyone.</Lede>

        <Scroll>
          <Choice $on={target === 'new'}>
            <input type="radio" checked={target === 'new'} onChange={() => setTarget('new')} />
            <Icon name="plus" size={14} /> A new pool
          </Choice>

          {pools.map((pool) => (
            <Choice key={pool._id} $on={target === pool._id}>
              <input type="radio" checked={target === pool._id} onChange={() => setTarget(pool._id)} />
              <Icon name={pool.source === 'discovery' ? 'spark' : 'list'} size={14} />
              {pool.name}
              <Count>{pool.count}</Count>
            </Choice>
          ))}
        </Scroll>

        {target === 'new' && (
          <Input
            value={name}
            maxLength={120}
            placeholder="Name this pool"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && ready) save(); }}
            aria-label="Name for the new pool"
            autoFocus
          />
        )}

        {error && <Problem>{error}</Problem>}

        <Row>
          <Button type="button" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button $variant="primary" type="button" onClick={save} disabled={busy || !ready}>
            {busy ? 'Saving…' : target === 'new' ? 'Create pool' : 'Add to pool'}
          </Button>
        </Row>
      </Sheet>
    </Backdrop>
  );
}

export default SaveToPoolModal;
